import { ReactElement, useState, useMemo } from 'react';
import { CircularProgress } from '@mui/material';

import { Nullable } from '~globals/types/commons';
import { getScheduleRouteById } from '~services/scheduleRoute';
import { ScheduleRouteExtended } from '~services/scheduleRoute/types';
import { getDriverCompanyAvailables } from '~services/driver';
import { DriverCompanyBase } from '~services/driver/types';
import { searchVehicles } from '~services/vehicle';
import { VehicleSearchItem } from '~services/vehicle/types';
import { useRequest } from '~hooks/useRequest';

import { DialogEditScheduleRouteProps } from './types';
import DialogEditScheduleRouteContent from './Content';
import { getDefaultVehiclesRequestPayload } from './utils';
import {
  DialogEditScheduleRouteContainer,
  DialogEditScheduleRouteLoadingContainer,
} from './styles';

const DialogEditScheduleRoute = ({
  open,
  onClose,
  routeScheduleId,
  onSuccessSubmit,
}: DialogEditScheduleRouteProps): ReactElement => {
  const [scheduleRouteInfo, setScheduleRouteInfo] =
    useState<Nullable<ScheduleRouteExtended>>(null);

  const [, loadingGetScheduleRouteById] = useRequest({
    request: getScheduleRouteById,
    payload: routeScheduleId,
    withPostSuccess: (response) => {
      const responseScheduleRouteInfo = response.data
        ?.data as ScheduleRouteExtended;

      setScheduleRouteInfo(responseScheduleRouteInfo);
    },
  });

  const [drivers, setDrivers] = useState<DriverCompanyBase[]>([]);

  const [, loadingGetDrivers] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    withPostSuccess: (response) => {
      const responseAvailablesDrivers = response.data?.data
        .results as DriverCompanyBase[];

      setDrivers(responseAvailablesDrivers);
    },
  });

  const apiTransactionScheduledDateTime = useMemo(
    () => scheduleRouteInfo?.apiTransactionScheduledDateTime,
    [scheduleRouteInfo?.apiTransactionScheduledDateTime],
  );

  const [vehicles, setVehicles] = useState<VehicleSearchItem[]>([]);

  const [, loadingGetVehicles] = useRequest({
    request: searchVehicles,
    isValidToRequest: Boolean(apiTransactionScheduledDateTime),
    initialLoading: Boolean(apiTransactionScheduledDateTime),
    payload: getDefaultVehiclesRequestPayload(
      apiTransactionScheduledDateTime as string,
    ),
    withPostSuccess: (response) => {
      const responseVehicles = response.data?.data
        .results as VehicleSearchItem[];

      setVehicles(responseVehicles);
    },
  });

  const loading = useMemo(
    () =>
      loadingGetScheduleRouteById || loadingGetDrivers || loadingGetVehicles,
    [loadingGetScheduleRouteById, loadingGetDrivers, loadingGetVehicles],
  );

  return (
    <DialogEditScheduleRouteContainer open={open}>
      {loading && (
        <DialogEditScheduleRouteLoadingContainer>
          <CircularProgress color="primary" disableShrink size={80} />
        </DialogEditScheduleRouteLoadingContainer>
      )}

      {!loading && scheduleRouteInfo && (
        <DialogEditScheduleRouteContent
          onClose={onClose}
          scheduleRouteInfo={scheduleRouteInfo}
          drivers={drivers}
          vehicles={vehicles}
          onSuccessSubmit={onSuccessSubmit}
        />
      )}
    </DialogEditScheduleRouteContainer>
  );
};

export default DialogEditScheduleRoute;
